/* SAR services not available in production api-services build
import type { ExternalTableSchema } from '@packages/table-builder';
import { IMPORT_EXPORT_SEMANTIC_IDS } from '@packages/table-builder/utils/importExportSemanticIds';

export const sarExerciseTable: ExternalTableSchema = {
  metadata: {
    title: 'SAR Exercises',
    entity: 'SarExercise',
    description: 'List of all the SARs Exercises',
  },

  dataSource: {
    endpoint: '/api/award/api/app/sar-exercise/sar-exercised-list',
    method: 'GET',
    rowIdField: 'id',
    responseMapping: {
      items: 'items',
      totalCount: 'totalCount',
    },
    requestParams: {
      Filter: 'table.searchText',
      MaxResultCount: 'table.pageSize',
      SkipCount: 'table.pageOffset',
      Sorting: 'table.sorting',
    },
    filters: [
      {
        name: 'StatusIds',
        placeholder: 'Exercise Status',
        type: 'asyncSelect',
        multiple: true,
        dataSource: {
          url: '/api/award/api/app/sar-exercise/sar-exercise-statuses',
          labelField: 'displayName',
          valueField: 'id',
        },
      },
      {
        name: 'FromDate',
        placeholder: 'From Date',
        type: 'date',
      },
      {
        name: 'ToDate',
        placeholder: 'To Date',
        type: 'date',
      },
      {
        name: 'SettlementTypeIds',
        placeholder: 'Settlement Type',
        type: 'asyncSelect',
        multiple: true,
        dataSource: {
          url: '/api/award/api/app/option-exercise/settlement-types',
          labelField: 'displayName',
          valueField: 'id',
        },
      },
      {
        name: 'GrantIds',
        placeholder: 'Grant',
        type: 'asyncSelect',
        multiple: true,
        dataSource: {
          url: '/api/award/api/app/grant/grant-list-queryable',
          labelField: 'grantName',
          valueField: 'id',
        },
      },
    ],
  },

  columns: [
    { field: 'awardNumber', header: '#Awards', type: 'text', width: 120, sortable: false },
    { field: 'participantName', header: 'Participant Details', type: 'text', width: 200, sortable: false },
    { field: 'grantName', header: 'Grant Name', type: 'text', width: 180, sortable: false },
    { field: 'exerciseDate', header: 'Exercise Date', type: 'date', width: 140, sortable: true },
    { field: 'exerciseQuantity', header: 'Shares', type: 'number', width: 100, sortable: false, align: 'center' },
    { field: 'settlementTypeName', header: 'Settlement Type', type: 'text', width: 140, sortable: false },
    { field: 'fmv', header: 'FMV $', type: 'currency', width: 110, sortable: false, align: 'center' },
    { field: 'exerciseStatus', header: 'Status', type: 'text', width: 130, sortable: false, visibility: 'picker' },
    { field: 'planName', header: 'Plan', type: 'text', width: 150, sortable: false, visibility: 'picker' },
    { field: 'basePrice', header: 'Base Price', type: 'currency', width: 110, sortable: false, align: 'center', visibility: 'picker' },
    { field: 'grossGain', header: 'Gross Gain', type: 'currency', width: 110, sortable: true, align: 'center', visibility: 'picker' },
    { field: 'cashPayoutAmount', header: 'Cash Payout', type: 'currency', width: 110, sortable: true, align: 'center', visibility: 'picker' },
    { field: 'incomeGain', header: 'Income Gain', type: 'currency', width: 110, sortable: false, align: 'center', visibility: 'picker' },
    { field: 'totalTaxes', header: 'Total Taxes', type: 'currency', width: 110, sortable: false, align: 'center', visibility: 'picker' },
    { field: 'moneyDueParticipant', header: 'Due Participant', type: 'currency', width: 120, sortable: false, align: 'center', visibility: 'picker' },
    { field: 'moneyDueCompany', header: 'Due Company', type: 'currency', width: 110, sortable: false, align: 'center', visibility: 'picker' },
    {
      field: 'isExerciseProcessable',
      header: 'Processable',
      type: 'badge',
      width: 110,
      sortable: false,
      align: 'center',
      conditionalValues: { true: 'Yes', false: 'No' },
      badge: { statusMap: { true: 'success', false: 'error' }, defaultVariant: 'error' },
      visibility: 'picker',
    },
    {
      field: 'isTaxCalculated',
      header: 'Tax Calculated',
      type: 'badge',
      width: 120,
      sortable: false,
      align: 'center',
      conditionalValues: { true: 'Yes', false: 'No' },
      badge: { statusMap: { true: 'success', false: 'error' }, defaultVariant: 'error' },
      visibility: 'picker',
    },
  ],

  actions: {
    toolbar: {
      secondary: [
        {
          label: 'Download Template',
          icon: 'download',
          type: 'download' as const,
          semanticId: IMPORT_EXPORT_SEMANTIC_IDS.downloadTemplate,
          children: [
            { id: 'DOWNLOAD_TEMPLATE_SAR_EXERCISE', label: 'Download SAR Exercise Template' },
          ],
        },
        {
          label: 'Import Template',
          icon: 'upload',
          type: 'upload' as const,
          semanticId: IMPORT_EXPORT_SEMANTIC_IDS.importTemplate,
          children: [
            {
              id: 'IMPORT_SAR_EXERCISE',
              label: 'Import Bulk XLS',
              semanticId: IMPORT_EXPORT_SEMANTIC_IDS.importBulkXls,
            },
          ],
        },
      ],
    },
    row: [
      { id: 'VIEW_SAR_EXERCISE_DETAIL', label: 'View Details' },
      { id: 'CALCULATE_SAR_EXERCISE_TAX', label: 'Calculate Tax' },
      { id: 'CANCEL_SAR_EXERCISE', label: 'Cancel', variant: 'danger' },
      { id: 'REVERSE_SAR_EXERCISE', label: 'Reverse & Delete', variant: 'danger' },
    ],
    bulk: [],
  },

  features: {
    search: { enabled: true, placeholder: 'Search participant, grant number, or grant name' },
    pagination: { mode: 'pagination', defaultSize: 10 },
    // selectAllPages omitted — bulk endpoints accept ID arrays only (no SarExerciseFilterService).
    selection: {
      keyField: 'id',
      selectable: { field: 'isExerciseProcessable', operator: 'EQUAL', value: true },
    },
    columnPreferences: { persistenceKey: 'SarExercise', resizable: true, reorderable: true },
    defaultSort: { field: 'exerciseDate', direction: 'desc' },
  },
};

*/
